/**
 * AWARE Health — CountryCard Component
 * Glassmorphism card for country selection in the sidebar.
 * Theme-aware and translated.
 */

import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/ThemeContext'

interface AlertCounts {
  watch: number
  warning: number
  emergency: number
}

interface CountryCardProps {
  code: string
  name: string
  flag: string
  isSelected: boolean
  isLoading?: boolean
  alertCounts?: AlertCounts
  onClick: () => void
}

export default function CountryCard({
  code,
  name,
  flag,
  isSelected,
  isLoading = false,
  alertCounts,
  onClick,
}: CountryCardProps) {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const hasAlerts = alertCounts && (alertCounts.watch + alertCounts.warning + alertCounts.emergency) > 0

  return (
    <button
      id={`country-card-${code}`}
      onClick={onClick}
      className={`country-card glass-card w-full text-left rounded-xl p-3 cursor-pointer ${isSelected ? 'selected' : ''}`}
      style={{
        background: isSelected
          ? (isDark ? 'rgba(20,184,166,0.08)' : 'rgba(13,148,136,0.06)')
          : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
        borderColor: isSelected
          ? (isDark ? 'rgba(20,184,166,0.5)' : 'rgba(13,148,136,0.35)')
          : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
        outline: 'none',
      }}
    >
      {/* Top row: flag + name + code */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22, lineHeight: 1 }}>{flag}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: isSelected
                ? (isDark ? '#5eead4' : '#0d9488')
                : (isDark ? '#cbd5e1' : '#475569'),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </div>
          <div style={{
            fontSize: 10,
            color: isDark ? '#475569' : '#94a3b8',
            marginTop: 1,
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            {code}
          </div>
        </div>

        {isSelected && (
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#14b8a6',
              flexShrink: 0,
              boxShadow: '0 0 8px #14b8a6',
            }}
          />
        )}
      </div>

      {/* Alert summary pills */}
      {isLoading && (
        <div
          className="skeleton"
          style={{ height: 18, marginTop: 8, borderRadius: 4 }}
        />
      )}

      {!isLoading && hasAlerts && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {alertCounts!.emergency > 0 && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#a855f7',
                background: 'rgba(168,85,247,0.15)',
                border: '1px solid rgba(168,85,247,0.3)',
                padding: '2px 6px',
                borderRadius: 10,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              ⚠ {alertCounts!.emergency} {t('alert.emergencyDetailed')}
            </span>
          )}
          {alertCounts!.warning > 0 && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#ef4444',
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.25)',
                padding: '2px 6px',
                borderRadius: 10,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              ▲ {alertCounts!.warning} {t('alert.warningDetailed')}
            </span>
          )}
          {alertCounts!.watch > 0 && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#f59e0b',
                background: 'rgba(245,158,11,0.12)',
                border: '1px solid rgba(245,158,11,0.25)',
                padding: '2px 6px',
                borderRadius: 10,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              ◆ {alertCounts!.watch} {t('alert.watchDetailed')}
            </span>
          )}
        </div>
      )}

      {!isLoading && !hasAlerts && isSelected && (
        <div
          style={{
            fontSize: 10,
            color: '#10b981',
            marginTop: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span>●</span> {t('sidebar.allRegionsNominal')}
        </div>
      )}
    </button>
  )
}
